<template>
  <text-box :label="boxLabel" icon="chart-bar" :warnings="warnings" :hideTitle="hideTitle">
    <template v-slot:info-dialog>
      <slot name="info-dialog"></slot>
    </template>
    <div class="msr-container">
      <div class="msr-chart">
        <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize :style="`width: 100%; height: ${chartHeight}px;`"/>
      </div>
      <div class="msr-legend">
        <div v-for="item in legendItems" :key="item.key" class="msr-legend-row">
          <button type="button"
                  class="msr-visibility"
                  :class="{ 'is-hidden': hiddenSeries[item.key] }"
                  :title="$t('components.ModelReportComponent.toggleSeriesVisibility')"
                  @click="toggleVisibility(item.key)">
            <span class="msr-dot" :style="hiddenSeries[item.key] ? `border: 2px solid ${item.color}; background: transparent;` : `background-color: ${item.color};`"></span>
            <span class="msr-name">{{ item.label }}</span>
          </button>
          <button type="button"
                  class="msr-sort"
                  :class="{ 'is-active': sortKey === item.key }"
                  :title="$t('components.ModelReportComponent.sortBy') + ': ' + item.label"
                  @click="toggleSort(item.key)">
            <v-icon small :color="sortKey === item.key ? 'primary' : 'grey lighten-1'">
              {{ sortKey === item.key ? (sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-swap-vertical' }}
            </v-icon>
          </button>
        </div>
      </div>
    </div>
  </text-box>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/toolbox'
import { graphic } from 'echarts/core'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'

import TextBox from '@/components/TextBox.vue'
import { roasUnitLabel, roasMantissa } from '@/utils/roasUnits.js'

const SPEND_COLOR = '#4B67B8'
const SALES_COLOR = 'rgb(194, 53, 49)'
const ROI_COLOR = '#43A047'

export default {
  data () {
    return {
      sortKey: 'spend',
      sortDir: 'desc',
      hiddenSeries: { spend: false, attributedSales: false, roi: false }
    }
  },
  computed: {
    spendLabel () {
      return this.$t('components.ModelReportComponent.spend')
    },
    attributedSalesLabel () {
      return this.$t('components.ModelReportComponent.attributedSales', { kpi: this.customKpiName })
    },
    roiLabel () {
      return this.$t('components.ModelReportComponent.roi')
    },
    title () {
      return this.$t('components.ModelReportComponent.mediaSpendAndReturn')
    },
    dualAxis () {
      return this.spendUnit !== this.salesUnit
    },
    // The ROAS is always shown — it is just attributed sales over spend. When the
    // two are measured in different units the ratio carries both of them, so the
    // legend, the tooltip and the Data View name it "ROAS (units/USD)".
    roiUnitLabel () {
      return roasUnitLabel(this.salesUnit, this.spendUnit)
    },
    roiLegendLabel () {
      return this.roiUnitLabel ? `${this.roiLabel} (${this.roiUnitLabel})` : this.roiLabel
    },
    // Caveats about the ROAS series: it carries a composite unit instead of being a return
    // multiple, and for a non-additive KPI its numerator is a per-period average while its
    // denominator is the period's total spend. Both are worded for this chart specifically,
    // where the ROAS is one series of three and the two bar series are sound either way.
    warnings () {
      return [
        this.roiUnitLabel
          ? this.$t('components.ModelReportComponent.roiCrossUnitWarning', { kpi: this.customKpiName, unit: this.roiUnitLabel })
          : null,
        this.nonAdditive
          ? this.$t('components.ModelReportComponent.roiNonAdditiveWarning', { kpi: this.customKpiName })
          : null
      ]
    },
    boxLabel () {
      if (this.dualAxis) {
        return this.title
      }
      return `${this.title} (${this.spendUnit})`
    },
    legendItems () {
      return [
        { key: 'spend', label: this.spendLabel, color: SPEND_COLOR },
        { key: 'attributedSales', label: this.attributedSalesLabel, color: SALES_COLOR },
        { key: 'roi', label: this.roiLegendLabel, color: ROI_COLOR }
      ]
    },
    sortedChannels () {
      let getValue
      if (this.sortKey === 'roi') {
        getValue = (channel) => {
          const spend = this.data.spend[channel] || 0
          const sales = this.data.attributedSales[channel] || 0
          return spend > 0 ? sales / spend : 0
        }
      } else if (this.sortKey === 'attributedSales') {
        getValue = (channel) => this.data.attributedSales[channel] || 0
      } else {
        getValue = (channel) => this.data.spend[channel] || 0
      }
      const sorted = [...this.data.channels].sort((a, b) => getValue(a) - getValue(b))
      // ECharts category axis: index 0 is at the bottom for horizontal bars.
      // 'desc' = biggest at top → leave ascending order so last index (top) is biggest.
      // 'asc' = smallest at top → reverse.
      if (this.sortDir === 'asc') {
        sorted.reverse()
      }
      return sorted
    },
    spendData () {
      return this.sortedChannels.map(channel => this.data.spend[channel] || 0)
    },
    attributedSalesData () {
      return this.sortedChannels.map(channel => this.data.attributedSales[channel] || 0)
    },
    spendTotal () {
      return Object.values(this.data.spend || {}).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    attributedSalesTotal () {
      return Object.values(this.data.attributedSales || {}).reduce((sum, v) => sum + (Number(v) || 0), 0)
    },
    chartHeight () {
      // The ROAS series claims a real slot in every category band (ECharts ignores
      // its barWidth: 0), so each channel needs the taller band whether or not the
      // chart is in dual-axis mode.
      const perChannel = 65
      const padding = this.dualAxis ? 120 : 80
      return Math.max(220, this.sortedChannels.length * perChannel + padding)
    },
    xAxisOption () {
      const baseAxisLabel = { formatter: this.formatValue }
      const splitLineStyle = { lineStyle: { color: '#E0E0E0', type: 'dashed' } }
      if (!this.dualAxis) {
        return {
          type: 'value',
          axisLabel: baseAxisLabel,
          axisLine: { show: true, lineStyle: { color: '#9E9E9E' } },
          axisTick: { show: true, lineStyle: { color: '#9E9E9E' } },
          splitLine: { show: true, ...splitLineStyle }
        }
      }
      return [
        {
          type: 'value',
          position: 'bottom',
          name: `${this.spendLabel} (${this.spendUnit})`,
          nameLocation: 'middle',
          nameGap: 30,
          axisLabel: baseAxisLabel,
          axisLine: { show: true, lineStyle: { color: SPEND_COLOR } },
          axisTick: { show: true, lineStyle: { color: SPEND_COLOR } },
          splitLine: { show: true, ...splitLineStyle }
        },
        {
          type: 'value',
          position: 'top',
          name: `${this.attributedSalesLabel} (${this.salesUnit})`,
          nameLocation: 'middle',
          nameGap: 30,
          axisLabel: baseAxisLabel,
          axisLine: { show: true, lineStyle: { color: SALES_COLOR } },
          axisTick: { show: true, lineStyle: { color: SALES_COLOR } },
          splitLine: { show: false }
        }
      ]
    },
    series () {
      const buildBar = (name, color, data, xAxisIndex) => ({
        name: name,
        type: 'bar',
        xAxisIndex: xAxisIndex,
        barCategoryGap: '20%',
        barGap: '10%',
        label: {
          show: true,
          position: 'right',
          formatter: this.formatBarLabel,
          color: color,
          rich: {
            pct: {
              color: '#9E9E9E',
              fontSize: 10,
              fontWeight: 'normal'
            }
          }
        },
        labelLayout: {
          hideOverlap: true
        },
        itemStyle: {
          color: new graphic.LinearGradient(2, 0, 0, 0, [
            { offset: 0, color: 'white' },
            { offset: 1, color: color }
          ]),
          borderRadius: [0, 2, 2, 0]
        },
        emphasis: {
          itemStyle: {
            color: new graphic.LinearGradient(3, 0, 0, 0, [
              { offset: 0, color: 'white' },
              { offset: 1, color: color }
            ])
          }
        },
        data: data
      })
      const all = [
        { key: 'spend', config: buildBar(this.spendLabel, SPEND_COLOR, this.spendData, 0) },
        { key: 'attributedSales', config: buildBar(this.attributedSalesLabel, SALES_COLOR, this.attributedSalesData, this.dualAxis ? 1 : 0) },
        {
          key: 'roi',
          // The series name stays the bare label: the tooltip identifies this row
          // by it, and the name is never drawn (the legend is the HTML block
          // beside the chart, not an ECharts legend).
          config: {
            name: this.roiLabel,
            type: 'bar',
            xAxisIndex: 0,
            barCategoryGap: '20%',
            barGap: '40%',
            barWidth: 0,
            itemStyle: { color: 'transparent', borderColor: 'transparent' },
            label: {
              show: true,
              position: 'right',
              distance: 12,
              padding: [2, 6],
              formatter: this.formatRoiLabel,
              color: ROI_COLOR,
              fontWeight: 'bold'
            },
            labelLayout: { hideOverlap: true },
            data: this.sortedChannels.map(() => 0)
          }
        }
      ]
      return all.filter(s => !this.hiddenSeries[s.key]).map(s => s.config)
    },
    options () {
      return {
        textStyle: {
          fontFamily: '"Roboto","Helvetica",sans-serif',
          lineHeight: 1.2381,
          fontWeight: 400
        },
        grid: {
          top: this.dualAxis ? 60 : 30,
          left: '3%',
          right: '8%',
          bottom: this.dualAxis ? 50 : 30,
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: this.formatTooltip,
          backgroundColor: '#FFFFFF',
          borderColor: '#E0E0E0',
          borderWidth: 1,
          padding: 0,
          textStyle: { color: '#212121', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 14px rgba(0,0,0,0.12); border-radius: 8px;'
        },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.simulationDetails.MarketingSpend.dataView'),
              lang: [
                this.$t('components.simulationDetails.MarketingSpend.dataView'),
                this.$t('components.simulationDetails.MarketingSpend.close'),
                this.$t('components.simulationDetails.MarketingSpend.refresh')
              ],
              optionToContent: this.buildDataView
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.title,
              title: this.$t('components.simulationDetails.MarketingSpend.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        xAxis: this.xAxisOption,
        yAxis: {
          type: 'category',
          axisLine: { show: true, lineStyle: { color: '#9E9E9E' } },
          axisTick: { show: false },
          splitLine: { show: false },
          data: this.sortedChannels
        },
        series: this.series
      }
    }
  },
  methods: {
    formatValue (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    formatValueFull (value) {
      return numbro(value).format({
        thousandSeparated: true,
        mantissa: 2,
        optionalMantissa: true,
        trimMantissa: true
      })
    },
    formatBarLabel (params) {
      const total = params.seriesName === this.spendLabel ? this.spendTotal : this.attributedSalesTotal
      const pct = this.formatPct(params.value, total, 0)
      return `${this.formatValue(params.value)} {pct|(${pct})}`
    },
    formatPct (value, total, decimals) {
      if (!total || total <= 0) {
        return '0%'
      }
      const pct = (value / total) * 100
      return numbro(pct).format({
        mantissa: decimals,
        optionalMantissa: true,
        trimMantissa: true
      }) + '%'
    },
    computeRoi (channel) {
      const spend = this.data.spend[channel] || 0
      const sales = this.data.attributedSales[channel] || 0
      if (spend <= 0) {
        return null
      }
      return sales / spend
    },
    formatRoi (value) {
      if (value === null || value === undefined || !isFinite(value)) {
        return '-'
      }
      return numbro(value).format({
        mantissa: roasMantissa(value),
        optionalMantissa: true,
        trimMantissa: true
      })
    },
    formatRoiLabel (params) {
      const channel = this.sortedChannels[params.dataIndex]
      return this.formatRoi(this.computeRoi(channel))
    },
    buildDataView () {
      // Plain-text (tab-separated) Data View, mirroring ECharts' default textarea so
      // the values stay easy to copy into a spreadsheet. We override the default only
      // to fix the ROI column: the ROI series carries a zero-only data array (its real
      // value is drawn via label formatter), so the built-in view would show 0.
      const spendHeader = this.spendUnit ? `${this.spendLabel} (${this.spendUnit})` : this.spendLabel
      const salesHeader = this.salesUnit ? `${this.attributedSalesLabel} (${this.salesUnit})` : this.attributedSalesLabel
      const header = ['', spendHeader, salesHeader, this.roiLegendLabel]
      // No thousand separators here so the values paste into a spreadsheet as plain numbers.
      const plain = value => numbro(value).format({
        thousandSeparated: false,
        mantissa: 2,
        optionalMantissa: true,
        trimMantissa: true
      })
      const lines = [header.join('\t')]
      this.sortedChannels.forEach(channel => {
        const row = [
          channel,
          plain(this.data.spend[channel] || 0),
          plain(this.data.attributedSales[channel] || 0),
          this.formatRoi(this.computeRoi(channel))
        ]
        lines.push(row.join('\t'))
      })
      const content = lines.join('\n').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const rows = Math.min(20, lines.length + 1)
      return `<textarea readonly style="width: 100%; height: ${rows * 1.4}em; box-sizing: border-box; padding: 12px 16px; border: none; resize: vertical; font-family: 'Roboto Mono', monospace; font-size: 13px; color: #212121; white-space: pre; overflow: auto;">${content}</textarea>`
    },
    toggleSort (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
    },
    toggleVisibility (key) {
      this.$set(this.hiddenSeries, key, !this.hiddenSeries[key])
    },
    formatTooltip (params) {
      if (!params || params.length === 0) {
        return ''
      }
      const channelName = params[0].axisValue
      const rows = []
      params.forEach(p => {
        if (p.seriesName === this.roiLabel) {
          rows.push({
            color: ROI_COLOR,
            label: this.roiLabel,
            value: this.formatRoi(this.computeRoi(channelName)),
            unit: this.roiUnitLabel || '',
            pct: ''
          })
          return
        }
        const isSpend = p.seriesName === this.spendLabel
        const total = isSpend ? this.spendTotal : this.attributedSalesTotal
        rows.push({
          color: isSpend ? SPEND_COLOR : SALES_COLOR,
          label: p.seriesName,
          value: this.formatValueFull(p.value),
          unit: isSpend ? this.spendUnit : this.salesUnit,
          pct: this.formatPct(p.value, total, 1)
        })
      })
      const headerStyle = 'padding: 10px 14px 8px; font-weight: 600; color: #212121; border-bottom: 1px solid #ECEFF1; letter-spacing: 0.2px;'
      const bodyStyle = 'padding: 8px 14px 10px; display: flex; flex-direction: column; gap: 6px;'
      const rowStyle = 'display: flex; align-items: center; justify-content: space-between; gap: 18px;'
      const labelGroupStyle = 'display: flex; align-items: center; gap: 8px;'
      const dotStyle = (color) => `display: inline-block; width: 9px; height: 9px; border-radius: 50%; background-color: ${color}; box-shadow: 0 0 0 2px ${color}1A;`
      const labelStyle = 'color: rgba(0,0,0,0.66); font-size: 12.5px;'
      const valueStyle = (color) => `font-weight: 600; color: ${color}; font-variant-numeric: tabular-nums;`
      const unitStyle = 'color: rgba(0,0,0,0.4); font-weight: 400; font-size: 11.5px; margin-left: 4px;'
      const pctStyle = 'color: rgba(0,0,0,0.5); font-weight: 500; font-size: 11.5px; margin-left: 6px;'
      let html = `<div style="min-width: 180px; font-family: 'Roboto', 'Helvetica', sans-serif;">`
      html += `<div style="${headerStyle}">${channelName}</div>`
      html += `<div style="${bodyStyle}">`
      rows.forEach(row => {
        html += `<div style="${rowStyle}">`
        html += `<div style="${labelGroupStyle}">`
        html += `<span style="${dotStyle(row.color)}"></span>`
        html += `<span style="${labelStyle}">${row.label}</span>`
        html += `</div>`
        html += `<div><span style="${valueStyle(row.color)}">${row.value}</span>`
        if (row.unit) {
          html += `<span style="${unitStyle}">${row.unit}</span>`
        }
        if (row.pct) {
          html += `<span style="${pctStyle}">${row.pct}</span>`
        }
        html += `</div>`
        html += `</div>`
      })
      html += `</div></div>`
      return html
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    spendUnit: {
      type: String,
      required: false,
      default: function () {
        return 'USD'
      }
    },
    salesUnit: {
      type: String,
      required: false,
      default: function () {
        return 'USD'
      }
    },
    customKpiName: {
      type: String,
      required: true
    },
    // True when the attributed sales handed in are per-period averages (a non-additive
    // output) while the spend is a period total, so the per-channel ROAS mixes both.
    nonAdditive: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    // Drops the header TextBox would otherwise draw, for a caller that titles the chart itself —
    // the same prop SalesDecomposition carries and for the same reason. The info-dialog slot lives
    // in that header, so a caller hiding it owns the explanation too.
    hideTitle: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    exportImageName: {
      type: String,
      required: false
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox
  }
}
</script>

<style scoped>
  .msr-container {
    display: flex;
    align-items: stretch;
    width: 100%;
  }
  .msr-chart {
    flex: 1 1 auto;
    min-width: 0;
  }
  .msr-legend {
    flex: 0 0 auto;
    width: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px 8px 8px;
  }
  .msr-legend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }
  .msr-visibility {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.87);
    transition: opacity 0.15s, background-color 0.15s;
    text-align: left;
    flex: 1 1 auto;
    min-width: 0;
  }
  .msr-visibility.is-hidden {
    opacity: 0.45;
  }
  .msr-visibility.is-hidden .msr-name {
    text-decoration: line-through;
  }
  .msr-visibility:hover {
    background: rgba(0, 0, 0, 0.04);
  }
  .msr-dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    flex-shrink: 0;
    box-sizing: border-box;
  }
  .msr-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .msr-sort {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    line-height: 0;
    flex-shrink: 0;
    transition: background-color 0.15s;
  }
  .msr-sort:hover {
    background: rgba(0, 0, 0, 0.04);
  }
  .msr-sort.is-active {
    background: rgba(25, 118, 210, 0.08);
  }
</style>
